import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  Calendar,
  Clock,
  ArrowLeft,
  Image as ImageIcon,
  UserPlus,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Eye,
  MapPin,
  ClipboardCheck,
} from "lucide-react";

import {
  useGetMaintenanceByTechnicianEmailQuery,
  useGetMaintenanceReportByIDQuery,
  useGetMaintenanceReportsQuery,
  useUpdatePreventiveMaintenanceMutation,
  useGiveEndTimeMutation,
  useGiveStartTimeMutation,
  useCompleteMaintenanceReportMutation,
} from "../../slices/maintenanceApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useSelector, useDispatch } from "react-redux";
import { assetApiSlice } from "../../slices/assetApiSlice";
import { createSelector } from "reselect";
import { toast } from "sonner";

// shadcn UI imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { formatImageUrl } from "../../lib/utils";

const WorkOrderModal = ({ order, onClose, refetchTechnicianSchedules }) => {
  const [teamMembers, setTeamMembers] = useState(order.teamMembers || []);
  const [newMember, setNewMember] = useState("");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState(
    order.status || "Pending",
  );
  const [images, setImages] = useState([]);
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);

  const [updateMaintenanceById] = useUpdatePreventiveMaintenanceMutation();
  const maintenanceID = order.maintenanceID;

  const [formData, setFormData] = useState({
    maintenanceReportID: "",
    startTime: "",
    endTime: "",
    finishedDate: "",
    totalCost: "",
    information: "",
    partsUsed: "",
    preventiveMaintenanceID: maintenanceID,
    images: [],
  });
  const [giveStartTime] = useGiveStartTimeMutation();
  const [giveEndTime] = useGiveEndTimeMutation();
  const [completeMaintenanceReport, { isLoading: posting }] =
    useCompleteMaintenanceReportMutation();

  const { data: maintenanceReport, refetch } = useGetMaintenanceReportByIDQuery(
    maintenanceID,
    {
      skip: !maintenanceID,
    },
  );
  const reportExists =
    Array.isArray(maintenanceReport) && maintenanceReport.length > 0;

  // Force refetch when maintenance ID changes to ensure fresh data
  useEffect(() => {
    if (maintenanceID) {
      refetch();
    }
  }, [maintenanceID, refetch]);

  useEffect(() => {
    if (reportExists) {
      const report = maintenanceReport[0];

      setFormData({
        maintenanceReportID: report.maintenanceReportID ?? "",
        startTime: report.startTime ?? "",
        endTime: report.endTime ?? "",
        finishedDate: report.finishedDate ?? "",
        totalCost: report.totalCost ?? "",
        information: report.information ?? "",
        partsUsed: Array.isArray(report.partsUsed)
          ? report.partsUsed.join(", ")
          : "",
        preventiveMaintenanceID: report.maintenanceID ?? "",
        images: report.images ?? [],
      });
    }
  }, [maintenanceReport, reportExists]);

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleAddMember = () => {
    if (newMember.trim() && !teamMembers.includes(newMember)) {
      setTeamMembers([...teamMembers, newMember]);
      setNewMember("");
    }
  };

  const handleRemoveMember = (index) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  if (!order) return null;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.warning("Upload Limit Exceeded", {
        description: "You can upload a maximum of 5 images.",
      });
      return;
    }
    setImageError("");
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    const { maintenanceReportID, totalCost, information, partsUsed } = formData;

    // Check if we have existing report data
    const hasExistingImages =
      reportExists && maintenanceReport?.[0]?.images?.length > 0;
    const hasExistingTechnicians =
      reportExists && maintenanceReport?.[0]?.technicians;

    // Validate required fields - only check new data if no existing data
    if (
      !maintenanceReportID ||
      !String(totalCost).trim() ||
      !String(information).trim() ||
      !String(partsUsed).trim() ||
      (!hasExistingTechnicians && teamMembers.length === 0) ||
      (!hasExistingImages && images.length === 0)
    ) {
      toast.warning("Please fill in all fields", {
        description:
          "Total Cost, Information, Parts Used, Team Members, and Images are required.",
      });
      return;
    }

    const sendData = new FormData();
    sendData.append("totalCost", String(totalCost).trim());
    sendData.append("information", String(information).trim());

    String(partsUsed)
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((part) => sendData.append("partsUsed", part));

    // Only append technicians if there are new ones (not already saved)
    if (!hasExistingTechnicians && teamMembers.length > 0) {
      teamMembers
        .map((tech) => tech.trim())
        .filter(Boolean)
        .forEach((tech) => sendData.append("technicians", tech));
    }

    // Only append images if there are new ones (not already saved)
    if (!hasExistingImages && images.length > 0) {
      images.forEach((img) => sendData.append("images", img));
    }

    try {
      // If status is being changed to Completed, perform those updates now
      if (selectedWorkStatus === "Completed") {
        await updateMaintenanceById({
          id: order.maintenanceID,
          maintenance: { status: "Completed" },
        }).unwrap();

        if (!formData.endTime) {
          const currentTime = new Date().toLocaleTimeString("en-GB", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          });
          const currentDate = new Date().toISOString().split("T")[0];

          await giveEndTime({
            maintenanceReportID: formData.maintenanceReportID,
            endTime: currentTime,
            finishedDate: currentDate,
          }).unwrap();
        }
      }

      await completeMaintenanceReport({
        maintenanceReportID,
        formData: sendData,
      }).unwrap();

      toast.success("Maintenance Report completed!");
      setFormData((f) => ({
        ...f,
        totalCost: "",
        information: "",
        partsUsed: "",
      }));
      setTeamMembers([]);
      setImages([]);
      refetch();
      refetchTechnicianSchedules();
      onClose();
    } catch (err) {
      console.error("Error completing maintenance report:", err);
      console.error("Error details:", {
        message: err?.data?.message,
        status: err?.status,
        error: err?.error,
        data: err?.data,
      });
      toast.error(
        err?.data?.message ||
          err?.message ||
          "Something went wrong. Please try again.",
      );
    }
  };

  const WorkOrder = [
    { value: "Pending", label: "Pending" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const getFilteredWorkStatusOptions = () => {
    switch (selectedWorkStatus) {
      case "Pending":
        return WorkOrder.filter(
          (option) =>
            option.value === "Pending" || option.value === "In Progress",
        );
      case "In Progress":
        return WorkOrder.filter(
          (option) =>
            option.value === "In Progress" || option.value === "Completed",
        );
      case "Completed":
        return WorkOrder.filter((option) => option.value === "Completed");
      default:
        return WorkOrder;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:w-[95vw] md:w-full max-h-[90vh] overflow-y-auto rounded-xl p-4 sm:p-6">
        <DialogHeader className="border-b pb-3 sm:pb-4 space-y-1.5">
          <DialogTitle className="text-lg sm:text-xl font-bold text-zinc-900">
            Maintenance Schedule Details
          </DialogTitle>
          <DialogDescription className="text-sm">
            View and update maintenance schedule information for this asset
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4 sm:space-y-6 pt-3 sm:pt-4"
          onSubmit={handleAdd}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="assetName" className="text-sm font-medium">
                Asset Name:
              </Label>
              <Input
                id="assetName"
                value={order.asset_Details.title}
                readOnly
                className="bg-gray-50 h-9 sm:h-10"
              />
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="area" className="text-sm font-medium">
                Area:
              </Label>
              <Input
                id="area"
                value={order.asset_Details.assetArea}
                readOnly
                className="bg-gray-50 h-9 sm:h-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label className="text-sm font-medium">Maintenance Dates:</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label
                    htmlFor="startDate"
                    className="text-[10px] font-bold text-zinc-400 uppercase tracking-tight"
                  >
                    Start
                  </Label>
                  <Input
                    id="startDate"
                    value={order.startDate}
                    readOnly
                    className="bg-zinc-50 border-zinc-200 h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="endDate"
                    className="text-[10px] font-bold text-zinc-400 uppercase tracking-tight"
                  >
                    End
                  </Label>
                  <Input
                    id="endDate"
                    value={order.endDate}
                    readOnly
                    className="bg-zinc-50 border-zinc-200 h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="timeStart" className="text-sm font-medium">
                Scheduled Time:
              </Label>
              <Input
                id="timeStart"
                value={order.timeStart || "Not specified"}
                readOnly
                className="bg-zinc-50 border-zinc-200 h-9 sm:h-10"
              />
            </div>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Description:
            </Label>
            <Textarea
              id="description"
              value={order.description}
              readOnly
              className="bg-gray-50 min-h-[80px] sm:min-h-[100px] text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Work Status:</Label>
            <Select
              value={selectedWorkStatus}
              onValueChange={async (newStatus) => {
                setSelectedWorkStatus(newStatus);

                try {
                  await updateMaintenanceById({
                    id: order.maintenanceID,
                    maintenance: { status: newStatus },
                  }).unwrap();

                  if (newStatus === "In Progress") {
                    const currentTime = new Date().toLocaleTimeString("en-GB", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    const response = await giveStartTime({
                      preventiveMaintenanceID: order.maintenanceID,
                      startTime: currentTime,
                    }).unwrap();

                    setFormData((prev) => ({
                      ...prev,
                      startTime: response.startTime,
                    }));
                  }

                  if (newStatus === "Completed") {
                    const currentTime = new Date().toLocaleTimeString("en-GB", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    const currentDate = new Date().toISOString().split("T")[0];

                    const response = await giveEndTime({
                      maintenanceReportID: formData.maintenanceReportID,
                      endTime: currentTime,
                      finishedDate: currentDate,
                    }).unwrap();

                    setFormData((prev) => ({
                      ...prev,
                      endTime: response.endTime,
                      finishedDate: response.finishedDate,
                    }));
                  }

                  refetch();
                  toast.success(`Status updated to "${newStatus}"`);
                } catch (err) {
                  console.error("❌ Failed to update work status:", err);
                  toast.error("Could not update status. Try again later.");
                }
              }}
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {getFilteredWorkStatusOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedWorkStatus === "Completed" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mt-2 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Please complete all required fields below (marked with{" "}
                  <span className="text-red-500">*</span>) before submitting the
                  report.
                </span>
              </p>
            )}
          </div>

          <div className="space-y-4 pt-4 border-t">
            <Label className="text-sm font-bold text-zinc-900 border-l-4 border-zinc-900 pl-2">
              Completion Details
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Execution Time:</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label
                      htmlFor="startTime"
                      className="text-[10px] font-bold text-zinc-400 uppercase tracking-tight"
                    >
                      Actual Start
                    </Label>
                    <Input
                      id="startTime"
                      type="time"
                      value={
                        reportExists && maintenanceReport?.startTime
                          ? maintenanceReport.startTime
                          : formData.startTime
                      }
                      readOnly
                      className="bg-zinc-50 border-zinc-200 h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label
                      htmlFor="endTime"
                      className="text-[10px] font-bold text-zinc-400 uppercase tracking-tight"
                    >
                      Actual End
                    </Label>
                    <Input
                      id="endTime"
                      type="time"
                      value={
                        reportExists && maintenanceReport?.endTime
                          ? maintenanceReport.endTime
                          : formData.endTime
                      }
                      readOnly
                      className="bg-zinc-50 border-zinc-200 h-9"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="finishedDate">Finished Date:</Label>
                <Input
                  id="finishedDate"
                  type="date"
                  value={
                    reportExists && maintenanceReport?.finishedDate
                      ? maintenanceReport.finishedDate
                      : formData.finishedDate
                  }
                  readOnly
                  className="bg-zinc-50 border-zinc-200"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="totalCost" className="flex items-center gap-1">
              Total Maintenance Cost: <span className="text-red-500">*</span>
            </Label>
            <Input
              id="totalCost"
              type="text"
              placeholder="Enter Total Cost"
              value={
                reportExists && maintenanceReport?.[0]?.totalCost
                  ? maintenanceReport[0].totalCost
                  : formData.totalCost
              }
              onChange={(e) => handleInputChange(e, "totalCost")}
              readOnly={reportExists && !!maintenanceReport?.[0]?.totalCost}
              className={
                !formData.totalCost && selectedWorkStatus === "Completed"
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="partsUsed" className="flex items-center gap-1">
              Spare Parts Used: <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="partsUsed"
              placeholder="Enter parts used (comma separated: e.g., wood, metal, screws)"
              value={
                reportExists && maintenanceReport?.[0]?.partsUsed
                  ? maintenanceReport[0].partsUsed
                  : formData.partsUsed
              }
              onChange={(e) =>
                setFormData({ ...formData, partsUsed: e.target.value })
              }
              readOnly={reportExists && !!maintenanceReport?.[0]?.partsUsed}
              className={
                !formData.partsUsed && selectedWorkStatus === "Completed"
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              Maintenance Images: <span className="text-red-500">*</span>
              <span className="text-xs text-muted-foreground font-normal">
                (At least 1 image required)
              </span>
            </Label>
            {reportExists && maintenanceReport[0]?.images?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {maintenanceReport[0].images.map((imgSrc, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={imgSrc}
                      alt={`Maintenance ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border shadow-sm"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  multiple
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img
                        src={
                          typeof img === "string"
                            ? formatImageUrl(img)
                            : URL.createObjectURL(img)
                        }
                        alt={`Preview ${idx}`}
                        className="w-full h-full object-cover rounded-lg border"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                        onClick={() => removeImage(idx)}
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <div
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-2 font-medium">
                        Add Images
                      </span>
                    </div>
                  )}
                </div>
                {imageError && (
                  <p className="text-red-500 text-xs">{imageError}</p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Label className="flex items-center gap-1">
              Team Members: <span className="text-red-500">*</span>
              <span className="text-xs text-muted-foreground font-normal">
                (At least 1 member required)
              </span>
            </Label>
            {reportExists && maintenanceReport[0]?.technicians ? (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
                {maintenanceReport[0].technicians
                  .split(",")
                  .map((member, index) => (
                    <Badge key={index} variant="secondary">
                      {member}
                    </Badge>
                  ))}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter technician name"
                    value={newMember}
                    onChange={(e) => setNewMember(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), handleAddMember())
                    }
                  />
                  <Button
                    type="button"
                    onClick={handleAddMember}
                    variant="outline"
                    style={{
                      backgroundColor:
                        "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                      color: "white",
                    }}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
                {teamMembers.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
                    {teamMembers.map((member, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {member}
                        <XCircle
                          className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
                          onClick={() => handleRemoveMember(index)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="additionalInfo" className="flex items-center gap-1">
              Additional Information: <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="additionalInfo"
              value={
                reportExists && maintenanceReport?.[0]?.information
                  ? maintenanceReport[0].information
                  : formData.information
              }
              onChange={(e) => handleInputChange(e, "information")}
              placeholder="Enter any additional observations or comments"
              readOnly={reportExists && !!maintenanceReport?.[0]?.information}
              className={`min-h-[100px] ${
                !formData.information && selectedWorkStatus === "Completed"
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
          </div>

          <DialogFooter>
            {reportExists &&
            maintenanceReport?.[0]?.finishedDate &&
            maintenanceReport?.[0]?.totalCost &&
            maintenanceReport?.[0]?.information &&
            maintenanceReport?.[0]?.partsUsed &&
            maintenanceReport?.[0]?.images?.length > 0 ? (
              <div className="w-full text-center py-2">
                <p className="text-green-600 font-medium flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Report Already Submitted
                </p>
              </div>
            ) : (
              <Button
                type="submit"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
                disabled={
                  posting ||
                  (selectedWorkStatus === "Completed" &&
                    (!formData.totalCost ||
                      !formData.information ||
                      !formData.partsUsed ||
                      teamMembers.length === 0 ||
                      images.length === 0))
                }
                className="w-full sm:w-auto px-8"
              >
                {posting ? "Processing..." : "Submit Report"}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const MaintenanceTaskList = ({
  searchTerm,
  setSearchTerm,
  filteredData,
  setSelectedOrder,
  selectedWorkStatus,
}) => {
  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by asset, location, or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white border-slate-200"
        />
      </div>

      {/* Tasks Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
        {filteredData.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold">Asset Name</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Start Date</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold text-center">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((order) => (
                <TableRow
                  key={order.maintenanceID}
                  onClick={() => setSelectedOrder(order)}
                  className={`hover:bg-[#305845]/5 cursor-pointer transition-all duration-200 group ${
                    order.hasIncompleteReport ? "bg-amber-50/30" : ""
                  }`}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {order.hasIncompleteReport && (
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      )}
                      <span>{order.asset_Details?.title || "Untitled"}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {order.asset_Details?.assetArea || "Not specified"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4" />
                      {order.startDate || "Not set"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.status} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                      }}
                      variant="outline"
                      size="sm"
                      className="gap-2 bg-[#305845] text-white hover:bg-[#305845]/90 border-none shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            searchTerm={searchTerm}
            selectedWorkStatus={selectedWorkStatus}
          />
        )}
      </div>

      {/* Tasks Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredData.length > 0 ? (
          filteredData.map((order) => (
            <Card
              key={order.maintenanceID}
              onClick={() => setSelectedOrder(order)}
              className={`border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group ${
                order.hasIncompleteReport ? "border-amber-200 bg-amber-50/10" : ""
              }`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-slate-900">
                      {order.asset_Details?.title || "Untitled"}
                    </h4>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">
                      {order.assetCode}
                    </p>
                  </div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {order.asset_Details?.assetArea || "Not set"}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    {order.startDate || "Not set"}
                  </div>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(order);
                  }}
                  className="w-full bg-[#305845] text-white hover:bg-[#305845]/90 text-xs h-9 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))
        ) : (
          <EmptyState
            searchTerm={searchTerm}
            selectedWorkStatus={selectedWorkStatus}
          />
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none shadow-none text-xs">
          <CheckCircle className="w-3 h-3 mr-1" />
          {status}
        </Badge>
      );
    case "in progress":
      return (
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none shadow-none text-xs">
          <Clock className="w-3 h-3 mr-1" />
          {status}
        </Badge>
      );
    case "pending":
      return (
        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none shadow-none text-xs">
          <Clock className="w-3 h-3 mr-1" />
          {status}
        </Badge>
      );
    default:
      return (
        <Badge className="bg-slate-100 text-slate-700 border-none shadow-none text-xs">
          {status}
        </Badge>
      );
  }
};

const EmptyState = ({ searchTerm, selectedWorkStatus }) => (
  <div className="py-16 text-center bg-white rounded-lg border border-slate-200">
    <div className="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-4">
      <ClipboardCheck className="h-8 w-8 text-slate-300" />
    </div>
    <h3 className="text-lg font-bold text-slate-900 mb-1">No Tasks Found</h3>
    <p className="text-slate-500 max-w-[280px] mx-auto text-sm">
      {searchTerm || selectedWorkStatus !== ""
        ? "Try adjusting your search terms or filters to find what you're looking for."
        : "You don't have any maintenance tasks assigned at the moment."}
    </p>
  </div>
);

const TechnicianMSchedule = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("all");
    }
  }, [searchParams]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(selectUserInfo, (userInfo) => {
    const email = userInfo?.username || userInfo?.email || "";
    return email === "null" ? "" : email;
  });
  const [data, setData] = useState([]);

  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const userEmail = userData?.user?.email || email;
  const {
    data: technicianSchedules,
    refetch: refetchTechnicianSchedules,
    isLoading,
  } = useGetMaintenanceByTechnicianEmailQuery(userEmail, {
    skip: !userEmail,
  });
  const { data: allMaintenanceReports } = useGetMaintenanceReportsQuery();
  const dispatch = useDispatch();

  // Helper function to check if a report is complete
  const isReportComplete = (report) => {
    if (!report) return false;
    return (
      report.totalCost &&
      report.totalCost > 0 &&
      report.information &&
      String(report.information).trim() !== "" &&
      report.partsUsed &&
      String(report.partsUsed).trim() !== "" &&
      report.images &&
      Array.isArray(report.images) &&
      report.images.length > 0 &&
      report.technicians &&
      String(report.technicians).trim() !== ""
    );
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAssetDetails = async () => {
      if (technicianSchedules?.length) {
        const assetPromises = technicianSchedules.map(async (schedule) => {
          try {
            const asset = await dispatch(
              assetApiSlice.endpoints.getAssetByAssetCode.initiate(
                schedule?.assetCode,
              ),
            ).unwrap();

            // Check if report exists and is complete
            const report = allMaintenanceReports?.find(
              (r) => r.preventiveMaintenanceID === schedule.maintenanceID,
            );
            const reportSubmitted = isReportComplete(report);

            return {
              ...schedule,
              asset_Details: asset,
              reportSubmitted: reportSubmitted,
              hasIncompleteReport:
                schedule.status === "Completed" && !reportSubmitted,
            };
          } catch (err) {
            console.error(
              `❌ Error fetching asset for ${schedule?.assetCode}:`,
              err,
            );
            return null;
          }
        });

        const results = await Promise.all(assetPromises);
        if (isMounted) {
          setData(results.filter(Boolean));
        }
      }
    };
    fetchAssetDetails();
    return () => {
      isMounted = false;
    };
  }, [technicianSchedules, allMaintenanceReports, dispatch]);

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading schedules...", { id: "loading-schedules" });
    } else {
      toast.dismiss("loading-schedules");
    }
  }, [isLoading]);

  const uniqueWorkStatuses = [
    { value: "all", label: "All Statuses" },
    ...Array.from(new Set(data.map((item) => item.status).filter(Boolean))).map(
      (status) => ({ value: status, label: status }),
    ),
  ];

  const filteredData = data
    .filter((item) => {
      const searchRecursively = (obj, term) => {
        if (!obj) return false;
        if (typeof obj !== "object")
          return obj.toString().toLowerCase().includes(term);
        return Object.values(obj).some((val) => searchRecursively(val, term));
      };

      const matchesSearch = searchRecursively(item, searchTerm.toLowerCase());
      const matchesStatus =
        selectedWorkStatus === "all" ||
        item.status?.toLowerCase() === selectedWorkStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by startDate in descending order (newest first)
      const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
      const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
      return dateB - dateA;
    });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
        {/* Simple Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
            Maintenance Schedule
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            Manage your assigned maintenance tasks
          </p>
        </div>

        <div className="mt-6">
          <MaintenanceTaskList
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filteredData={filteredData}
            setSelectedOrder={setSelectedOrder}
            selectedWorkStatus={selectedWorkStatus}
          />
        </div>


        {/* Modal */}
        {selectedOrder && (
          <WorkOrderModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            refetchTechnicianSchedules={refetchTechnicianSchedules}
          />
        )}
      </div>
    </div>
  );
};

export default TechnicianMSchedule;
